import { IFileCategory, INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { useInfiniteGetNodeContentQueryParams } from '@/api/nodes/queries/get-node-content-query'
import { useCallback, useMemo, useState } from 'react'
import { useStore } from 'zustand'
import { IAssetSections, IDriveFilterState } from '@/features/drive/functionalities/filters/filter-state'
import { StoreApi } from 'zustand'

export type IDirectoryExPostFilters = {
    fileCategories?: IFileCategory[]
    fileTypes?: string[]
    disabledFileCategories?: IFileCategory[]
    disableMixedFileTypes?: boolean
    selectedFileCategory?: IFileCategory
    onlyDirectories?: boolean
}

interface IUseDirectoryContextContentParams {
    nodePath: string
    filterStore: StoreApi<IDriveFilterState>
    exPostFilters: IDirectoryExPostFilters
}

export type IDirectoryContentMaps = {
    contentPathToNodeMap: IDirectoryContentPathToNodeMap
    contentPathToIndexMap: IDirectoryContentPathToIndexMap
    contentIndexToPathMap: IDirectoryContentIndexToPathMap
    isLoading: boolean
}

export type IDirectoryContentPathToNodeMap = Record<string, INode>
export type IDirectoryContentPathToIndexMap = Record<string, number>
export type IDirectoryContentIndexToPathMap = Record<number, string>

export const useDirectoryContextContent = ({
    nodePath,
    filterStore,
    exPostFilters,
}: IUseDirectoryContextContentParams) => {
    const { globalSearch, onlyFavorites, search, ...setters } = useStore(filterStore)

    const infiniteQueryParams = useMemo(
        () => ({
            nodePath: nodePath,
            searchParams: {
                globalSearch,
                onlyFavorites,
                search,
            },
        }),
        [nodePath, globalSearch, onlyFavorites, search]
    )

    const [rawAllContent, setAllContent] = useState<Record<IAssetSections, INode[]>>({
        DIRECTORY: [],
        OTHER: [],
    })

    const exchangeContent = useCallback((type: IAssetSections, content: INode[]) => {
        setAllContent((prev) => {
            return {
                ...prev,
                [type]: content,
            }
        })
    }, [])

    const allContent = useMemo(() => {
        return [...rawAllContent.DIRECTORY, ...rawAllContent.OTHER]
    }, [rawAllContent])

    const { queryKey: nodeContentQueryKey } = useInfiniteGetNodeContentQueryParams(infiniteQueryParams)

    return {
        setters,
        allContent: allContent,
        exchangeContent,
        nodeContentQueryKey,
        exPostFilters,
        query: infiniteQueryParams.searchParams,
    }
}
